import { useState, useEffect, useCallback } from "react";
import { Briefcase, User, MapPin, Clock, PoundSterling, ChevronDown, ChevronUp, Plus, X, FileText, UserCheck, Lock, Unlock, ShieldCheck } from "lucide-react";
import { useSEO } from "@/hooks/use-seo";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AYRSHIRE_TOWNS, useTown } from "@/hooks/use-town";
import { timeAgo } from "@/lib/time-ago";

const BASE_URL = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";

const TOWNS = AYRSHIRE_TOWNS.filter(t => t !== "All Towns");

const EMPLOYMENT_TYPES = [
  { key: "full_time", label: "Full Time" },
  { key: "part_time", label: "Part Time" },
  { key: "contract", label: "Contract" },
  { key: "temporary", label: "Temporary" },
  { key: "apprenticeship", label: "Apprenticeship" },
];

const EMPLOYMENT_LABEL: Record<string, string> = Object.fromEntries(EMPLOYMENT_TYPES.map(t => [t.key, t.label]));

const VACANCY_CATEGORIES = [
  "Administration & Office",
  "Agriculture & Farming",
  "Automotive & Mechanics",
  "Bar & Pub Work",
  "Beauty & Wellness",
  "Care & Social Work",
  "Catering & Food Service",
  "Cleaning & Facilities",
  "Construction & Trades",
  "Customer Service",
  "Delivery & Courier",
  "Design & Creative",
  "Driving & Transport",
  "Education & Childcare",
  "Engineering",
  "Events & Entertainment",
  "Finance & Accounting",
  "Fitness & Leisure",
  "Gardening & Landscaping",
  "Graduate & Entry Level",
  "Healthcare & Medical",
  "Hotel & Tourism",
  "IT & Technology",
  "Legal & Compliance",
  "Management & Leadership",
  "Manufacturing & Production",
  "Marketing & PR",
  "Media & Journalism",
  "Plumbing & Heating",
  "Retail & Sales",
  "Science & Research",
  "Security",
  "Skilled Trades",
  "Voluntary & Charity",
  "Warehouse & Logistics",
  "Other",
];

function SearchableCategory({ value, onChange, accentClass = "border-primary focus:border-primary" }: { value: string; onChange: (v: string) => void; accentClass?: string }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const filtered = search.trim()
    ? VACANCY_CATEGORIES.filter(c => c.toLowerCase().includes(search.toLowerCase()))
    : VACANCY_CATEGORIES;
  return (
    <div className="relative">
      <div
        className={`flex items-center gap-2 border rounded-xl px-4 py-3 text-sm cursor-pointer bg-white ${open ? "border-primary" : "border-slate-200"}`}
        onClick={() => { setOpen(o => !o); setSearch(""); }}
      >
        <span className={`flex-1 ${value ? "text-slate-800" : "text-slate-400"}`}>{value || "Select a category…"}</span>
        <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search categories…"
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg outline-none focus:border-primary"
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div className="max-h-52 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <p className="text-slate-400 text-sm text-center py-3">No results for "{search}"</p>
            ) : filtered.map(cat => (
              <button
                key={cat}
                type="button"
                onClick={() => { onChange(cat); setOpen(false); setSearch(""); }}
                className={`w-full text-left text-sm px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors ${value === cat ? "font-semibold text-primary bg-primary/5" : "text-slate-700"}`}
              >
                {value === cat && <span className="mr-1">✓</span>}{cat}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface JobVacancy {
  id: number;
  companyName: string;
  title: string;
  description: string;
  category: string;
  town: string;
  employmentType: string;
  salary?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  approved: boolean;
  createdAt: string;
}

interface SeekerProfile {
  id: number;
  name: string;
  bio?: string | null;
  category: string;
  town: string;
  employmentType: string;
  cvUrl?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  isAvailable: boolean;
  createdAt: string;
}

function authHeaders(token: string | null): Record<string, string> {
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h;
}

function VacancyCard({ item }: { item: JobVacancy }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-5"
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
            <Briefcase size={20} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-slate-800 text-base leading-tight">{item.title}</h3>
            <p className="text-primary font-semibold text-sm mt-0.5">{item.companyName}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                <MapPin size={10} /> {item.town}
              </span>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 dark:bg-amber-900/20 px-2.5 py-1 rounded-full">
                <Clock size={10} /> {EMPLOYMENT_LABEL[item.employmentType] ?? item.employmentType}
              </span>
              {item.salary && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
                  <PoundSterling size={10} /> {item.salary}
                </span>
              )}
            </div>
          </div>
          <div className="flex-shrink-0 flex items-center gap-2">
            <span className="text-xs text-slate-400">{timeAgo(item.createdAt)}</span>
            {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </div>
        </div>
      </button>
      {expanded && (
        <div className="px-5 pb-5 border-t border-slate-100 pt-4 space-y-3">
          <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">{item.description}</p>
          {(item.contactEmail || item.contactPhone) && (
            <div className="flex flex-wrap gap-2 pt-2">
              {item.contactPhone && (
                <a href={`tel:${item.contactPhone}`} className="inline-flex items-center gap-1.5 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-xl transition-colors">
                  📞 Call Now
                </a>
              )}
              {item.contactEmail && (
                <a href={`mailto:${item.contactEmail}`} className="inline-flex items-center gap-1.5 text-xs font-bold bg-primary hover:bg-primary/90 text-white px-3 py-2 rounded-xl transition-colors">
                  ✉ Email Now
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SeekerCard({ item }: { item: SeekerProfile }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full text-left p-5">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
            <User size={20} className="text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-slate-800 text-base leading-tight">{item.name}</h3>
            <p className="text-emerald-600 font-semibold text-sm mt-0.5">{item.category}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                <MapPin size={10} /> {item.town}
              </span>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 dark:bg-amber-900/20 px-2.5 py-1 rounded-full">
                <Clock size={10} /> {EMPLOYMENT_LABEL[item.employmentType] ?? item.employmentType}
              </span>
              {item.cvUrl && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-purple-700 bg-purple-50 px-2.5 py-1 rounded-full">
                  <FileText size={10} /> CV Available
                </span>
              )}
            </div>
          </div>
          <div className="flex-shrink-0 flex items-center gap-2">
            <span className="text-xs text-slate-400">{timeAgo(item.createdAt)}</span>
            {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </div>
        </div>
      </button>
      {expanded && (
        <div className="px-5 pb-5 border-t border-slate-100 pt-4 space-y-3">
          {item.bio && <p className="text-slate-600 text-sm leading-relaxed">{item.bio}</p>}
          {item.cvUrl && (
            <a
              href={item.cvUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-xl transition-colors"
            >
              <FileText size={12} /> View CV / Portfolio
            </a>
          )}
          {(item.contactEmail || item.contactPhone) && (
            <div className="flex flex-wrap gap-2 pt-1">
              {item.contactPhone && (
                <a href={`tel:${item.contactPhone}`} className="inline-flex items-center gap-1.5 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-xl transition-colors">
                  📞 Call Now
                </a>
              )}
              {item.contactEmail && (
                <a href={`mailto:${item.contactEmail}`} className="inline-flex items-center gap-1.5 text-xs font-bold bg-primary hover:bg-primary/90 text-white px-3 py-2 rounded-xl transition-colors">
                  ✉ Email Now
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PostJobModal({ onClose, token }: { onClose: () => void; token: string | null }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState({ companyName: "", title: "", description: "", category: "", town: "Ayr", employmentType: "full_time", salary: "", contactEmail: "", contactPhone: "" });

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE_URL}/api/job-vacancies`, { method: "POST", headers: authHeaders(token), body: JSON.stringify({ ...form, salary: form.salary || null, contactEmail: form.contactEmail || null, contactPhone: form.contactPhone || null }) });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["job-vacancies"] }); toast({ title: "Vacancy submitted!", description: "We'll review and approve it shortly." }); onClose(); },
    onError: () => toast({ title: "Error", description: "Could not submit vacancy.", variant: "destructive" }),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <span className="font-bold text-slate-800 text-base">Post a Job Vacancy</span>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {[
            { label: "Company / Organisation *", key: "companyName", placeholder: "e.g. Ayrshire Council" },
            { label: "Job Title *", key: "title", placeholder: "e.g. Retail Assistant" },
            { label: "Salary / Rate (optional)", key: "salary", placeholder: "e.g. £12/hr or £24,000 – £28,000" },
            { label: "Contact Email", key: "contactEmail", placeholder: "jobs@example.com" },
            { label: "Contact Phone", key: "contactPhone", placeholder: "01292 000 000" },
          ].map(({ label, key, placeholder }) => (
            <div key={key}>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">{label}</label>
              <input value={form[key as keyof typeof form]} onChange={e => setForm({ ...form, [key]: e.target.value })} placeholder={placeholder} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-primary transition-colors" />
            </div>
          ))}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Job Category *</label>
            <SearchableCategory value={form.category} onChange={v => setForm({ ...form, category: v })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Description *</label>
            <textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Describe the role, responsibilities, and requirements..." className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-primary transition-colors resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Employment Type</label>
              <select value={form.employmentType} onChange={e => setForm({ ...form, employmentType: e.target.value })} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-primary bg-white">
                {EMPLOYMENT_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Town</label>
              <select value={form.town} onChange={e => setForm({ ...form, town: e.target.value })} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-primary bg-white">
                {TOWNS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <button
            onClick={() => { if (!form.companyName || !form.title || !form.description || !form.category) { return; } void mutateAsync(); }}
            disabled={isPending || !form.companyName || !form.title || !form.description || !form.category}
            className="w-full bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold py-3.5 rounded-xl transition-colors"
          >
            {isPending ? "Submitting…" : "Submit Vacancy"}
          </button>
          <p className="text-center text-xs text-slate-400">Vacancies are reviewed before going live.</p>
        </div>
      </div>
    </div>
  );
}

function PostSeekerModal({ onClose, token }: { onClose: () => void; token: string | null }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", bio: "", category: "", town: "Ayr", employmentType: "full_time", contactEmail: "", contactPhone: "" });

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE_URL}/api/seeker-profiles`, { method: "POST", headers: authHeaders(token), body: JSON.stringify({ ...form, bio: form.bio || null, contactEmail: form.contactEmail || null, contactPhone: form.contactPhone || null }) });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["seeker-profiles"] }); toast({ title: "Profile posted!", description: "Local employers can now find you." }); onClose(); },
    onError: () => toast({ title: "Error", description: "Could not save profile.", variant: "destructive" }),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <span className="font-bold text-slate-800 text-base">I'm Looking for Work</span>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {[
            { label: "Your Full Name *", key: "name", placeholder: "Full name" },
            { label: "Contact Email", key: "contactEmail", placeholder: "you@example.com" },
            { label: "Contact Phone", key: "contactPhone", placeholder: "07700 900 000" },
          ].map(({ label, key, placeholder }) => (
            <div key={key}>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">{label}</label>
              <input value={form[key as keyof typeof form]} onChange={e => setForm({ ...form, [key]: e.target.value })} placeholder={placeholder} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-primary transition-colors" />
            </div>
          ))}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">What type of work? *</label>
            <SearchableCategory value={form.category} onChange={v => setForm({ ...form, category: v })} accentClass="border-emerald-500" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">About You (optional)</label>
            <textarea rows={3} value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} placeholder="Brief summary of your experience and skills..." className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-primary transition-colors resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Employment Type</label>
              <select value={form.employmentType} onChange={e => setForm({ ...form, employmentType: e.target.value })} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-primary bg-white">
                {EMPLOYMENT_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Town</label>
              <select value={form.town} onChange={e => setForm({ ...form, town: e.target.value })} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-primary bg-white">
                {TOWNS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <button
            onClick={() => { if (!form.name || !form.category) return; void mutateAsync(); }}
            disabled={isPending || !form.name || !form.category}
            className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold py-3.5 rounded-xl transition-colors"
          >
            {isPending ? "Posting…" : "Post My Availability"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface EmployerStatus { verified: boolean; fee: number; isFree: boolean; publishableKey: string | null; }

export default function Vacancies() {
  useSEO({ title: "Jobs & Vacancies in Ayrshire", description: "Find local job vacancies and post your availability in Ayrshire. Connecting employers and job seekers across Ayr, Kilmarnock, Irvine and more." });
  const { townParam } = useTown();
  const browseQuery = townParam ? `?town=${encodeURIComponent(townParam)}` : "";
  const { user } = useAuth();
  const token = localStorage.getItem("ayrshire_token");
  const { toast } = useToast();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<"vacancies" | "seekers">("vacancies");
  const [showPostJob, setShowPostJob] = useState(false);
  const [showPostSeeker, setShowPostSeeker] = useState(false);
  const [filterCategory, setFilterCategory] = useState("");
  const [filterType, setFilterType] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  const { data: employerStatus, refetch: refetchEmployerStatus } = useQuery<EmployerStatus>({
    queryKey: ["employer-access-status", token],
    queryFn: async () => {
      const res = await fetch(`${BASE_URL}/api/employer-access/status`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      return res.json() as Promise<EmployerStatus>;
    },
    staleTime: 60000,
  });

  // Handle redirect back from Stripe
  const verifyEmployerPayment = useCallback(async (sessionId: string) => {
    if (!token) return;
    try {
      const res = await fetch(`${BASE_URL}/api/employer-access/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sessionId }),
      });
      if (res.ok) {
        await refetchEmployerStatus();
        void qc.invalidateQueries({ queryKey: ["seeker-profiles"] });
        toast({ title: "Employer access unlocked!", description: "You can now view all candidate profiles and CVs." });
      }
    } catch { /* silent */ }
  }, [token, refetchEmployerStatus, qc, toast]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const success = params.get("payment_success");
    if (success === "1" && sessionId) {
      void verifyEmployerPayment(sessionId);
      // Clean up URL
      const url = new URL(window.location.href);
      url.searchParams.delete("payment_success");
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", url.toString());
      setActiveTab("seekers");
    }
  }, [verifyEmployerPayment]);

  const handleUnlockEmployerAccess = async () => {
    if (!user) { toast({ title: "Sign in required", description: "Please sign in to unlock employer access." }); return; }
    if (!employerStatus) return;
    setUnlocking(true);
    try {
      if (employerStatus.isFree) {
        const res = await fetch(`${BASE_URL}/api/employer-access/checkout`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ successUrl: window.location.href, cancelUrl: window.location.href }),
        });
        const data = await res.json() as { alreadyVerified?: boolean };
        if (data.alreadyVerified) {
          await refetchEmployerStatus();
          void qc.invalidateQueries({ queryKey: ["seeker-profiles"] });
        }
        return;
      }
      const res = await fetch(`${BASE_URL}/api/employer-access/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ successUrl: window.location.href, cancelUrl: window.location.href }),
      });
      const data = await res.json() as { url?: string; alreadyVerified?: boolean; error?: string };
      if (data.alreadyVerified) { await refetchEmployerStatus(); void qc.invalidateQueries({ queryKey: ["seeker-profiles"] }); return; }
      if (data.error) { toast({ title: "Payment unavailable", description: data.error, variant: "destructive" }); return; }
      if (data.url) window.location.href = data.url;
    } catch {
      toast({ title: "Error", description: "Could not start payment. Please try again.", variant: "destructive" });
    } finally {
      setUnlocking(false);
    }
  };

  const { data: vacData, isLoading: vacLoading } = useQuery<{ items: JobVacancy[] }>({
    queryKey: ["job-vacancies", townParam],
    queryFn: async () => {
      const res = await fetch(`${BASE_URL}/api/job-vacancies${browseQuery}`);
      return res.json() as Promise<{ items: JobVacancy[] }>;
    },
    staleTime: 30000,
  });

  const { data: seekerData, isLoading: seekerLoading } = useQuery<{ items: SeekerProfile[]; employerVerified?: boolean }>({
    queryKey: ["seeker-profiles", token, townParam],
    queryFn: async () => {
      const res = await fetch(`${BASE_URL}/api/seeker-profiles${browseQuery}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      return res.json() as Promise<{ items: SeekerProfile[]; employerVerified?: boolean }>;
    },
    staleTime: 30000,
  });

  const employerVerified = employerStatus?.verified ?? seekerData?.employerVerified ?? false;

  const allVacancies = vacData?.items ?? [];
  const allSeekers = seekerData?.items ?? [];
  const vacancies = allVacancies
    .filter(v => !filterCategory || v.category === filterCategory)
    .filter(v => !filterType || v.employmentType === filterType);
  const seekers = allSeekers
    .filter(s => !filterCategory || s.category === filterCategory)
    .filter(s => !filterType || s.employmentType === filterType);
  const isLoading = activeTab === "vacancies" ? vacLoading : seekerLoading;

  const handlePostClick = (type: "job" | "seeker") => {
    if (!user) { toast({ title: "Sign in required", description: "Please sign in to post." }); return; }
    if (type === "job") setShowPostJob(true);
    else setShowPostSeeker(true);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm text-slate-500 mb-1">Local opportunities across Ayrshire</p>
              <h1 className="text-3xl font-bold text-primary">Jobs & Vacancies</h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePostClick("seeker")}
                className="flex items-center gap-2 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold px-4 py-2.5 rounded-xl transition-colors"
              >
                <UserCheck size={16} />
                I'm Looking for Work
              </button>
              <button
                onClick={() => handlePostClick("job")}
                className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors"
              >
                <Plus size={18} />
                Post a Vacancy
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-6 bg-slate-100 rounded-xl p-1 w-fit">
            <button
              onClick={() => { setActiveTab("vacancies"); setFilterCategory(""); setFilterType(""); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === "vacancies" ? "bg-white text-primary shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              <Briefcase size={14} /> Hiring Now {allVacancies.length > 0 && <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded-full text-xs">{allVacancies.length}</span>}
            </button>
            <button
              onClick={() => { setActiveTab("seekers"); setFilterCategory(""); setFilterType(""); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === "seekers" ? "bg-white text-emerald-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              <User size={14} /> Looking for Work {allSeekers.length > 0 && <span className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full text-xs">{allSeekers.length}</span>}
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mt-5">
            <div className="relative">
              <select
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value)}
                className="appearance-none border border-slate-200 bg-white rounded-xl pl-4 pr-9 py-2.5 text-sm font-medium text-slate-700 outline-none focus:border-primary cursor-pointer"
              >
                <option value="">All Categories</option>
                {VACANCY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
            <div className="relative">
              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                className="appearance-none border border-slate-200 bg-white rounded-xl pl-4 pr-9 py-2.5 text-sm font-medium text-slate-700 outline-none focus:border-primary cursor-pointer"
              >
                <option value="">All Types</option>
                {EMPLOYMENT_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
            {(filterCategory || filterType) && (
              <button
                onClick={() => { setFilterCategory(""); setFilterType(""); }}
                className="flex items-center gap-1.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-500 text-sm font-medium px-3.5 py-2.5 rounded-xl transition-colors"
              >
                <X size={13} /> Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl p-5 shadow-sm animate-pulse">
                <div className="flex gap-4">
                  <div className="w-11 h-11 bg-slate-200 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-2/3" />
                    <div className="h-3 bg-slate-200 rounded w-1/3" />
                    <div className="flex gap-2 mt-2">
                      <div className="h-5 w-16 bg-slate-200 rounded-full" />
                      <div className="h-5 w-20 bg-slate-200 rounded-full" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : activeTab === "vacancies" ? (
          vacancies.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-20 h-20 rounded-3xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Briefcase size={32} className="text-primary" />
              </div>
              <p className="text-slate-600 font-semibold text-lg">No vacancies listed yet</p>
              <p className="text-slate-400 text-sm mt-1">Be the first to post a job opportunity in Ayrshire.</p>
              <button onClick={() => handlePostClick("job")} className="mt-6 bg-primary hover:bg-primary/90 text-white font-bold px-6 py-3 rounded-xl transition-colors">
                Post the First Vacancy
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">{vacancies.length} {vacancies.length === 1 ? "vacancy" : "vacancies"} available</p>
              {vacancies.map(v => <VacancyCard key={v.id} item={v} />)}
            </div>
          )
        ) : (
          seekers.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-20 h-20 rounded-3xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                <User size={32} className="text-emerald-600" />
              </div>
              <p className="text-slate-600 font-semibold text-lg">Nobody has posted their availability yet</p>
              <p className="text-slate-400 text-sm mt-1">Are you looking for work in Ayrshire? Post your profile and get noticed by local employers.</p>
              <button onClick={() => handlePostClick("seeker")} className="mt-6 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-3 rounded-xl transition-colors">
                Post My Availability
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Employer access gate banner */}
              {!employerVerified && (
                <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Lock size={22} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-primary text-base">Contact details & CVs are locked</p>
                    <p className="text-slate-600 text-sm mt-0.5">
                      {user
                        ? `Unlock full employer access for a one-off fee${employerStatus && !employerStatus.isFree ? ` of £${(employerStatus.fee / 100).toFixed(2)}` : " (free)"} to view candidate contact details and download CVs.`
                        : "Sign in and unlock employer access to view candidate contact details and download CVs."}
                    </p>
                  </div>
                  <button
                    onClick={() => void handleUnlockEmployerAccess()}
                    disabled={unlocking}
                    className="flex-shrink-0 flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold px-5 py-2.5 rounded-xl transition-colors whitespace-nowrap"
                  >
                    {unlocking ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Unlock size={15} />}
                    {employerStatus?.isFree ? "Get Free Access" : `Unlock${employerStatus && !employerStatus.isFree ? ` — £${(employerStatus.fee / 100).toFixed(2)}` : ""}`}
                  </button>
                </div>
              )}

              {employerVerified && (
                <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5 w-fit text-sm font-semibold">
                  <ShieldCheck size={15} /> Employer access active — all contact details visible
                </div>
              )}

              <p className="text-sm text-slate-500">{seekers.length} {seekers.length === 1 ? "person" : "people"} looking for work</p>
              {seekers.map(s => (
                <div key={s.id} className="relative">
                  <SeekerCard item={s} />
                  {!employerVerified && (s.contactEmail || s.contactPhone || s.cvUrl) === null && (
                    <div className="mt-2 px-5 pb-4">
                      <button
                        onClick={() => void handleUnlockEmployerAccess()}
                        className="flex items-center gap-1.5 text-xs font-semibold text-primary/70 hover:text-primary transition-colors"
                      >
                        <Lock size={11} /> Unlock to view contact details & CV
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {showPostJob && <PostJobModal onClose={() => setShowPostJob(false)} token={token} />}
      {showPostSeeker && <PostSeekerModal onClose={() => setShowPostSeeker(false)} token={token} />}
    </div>
  );
}
